import { createFileRoute, Link, Outlet, useParams } from '@tanstack/react-router'
import { Suspense, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useLingui } from '@lingui/react/macro'
import { trpc } from '@/main'
import { useRootSearch } from '@/hooks/use-root-search'
import { useIsMobile } from '@/hooks/use-mobile'
import { useHydrated } from '@/hooks/use-hydrated'
import { usePermissions } from '@/hooks/use-permissions'
import { UsersIcon } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ReportPager } from './-components/report/report-pager'
import { useTransitionPreviewFrom } from '@/components/custom/route-transition/route-preview'
import { ListPanelBodySkeleton, MembersPanelSkeleton, ReportTabsSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { ReportTabs } from './-components/report/report-tabs'
import { OrgsUsersPanel } from './-components/orgs/orgs-users-panel'
import { OrgsGroupsOverview } from './-components/orgs/orgs-groups-overview'
import { OrgGroupForm } from './-components/orgs/org-group-form'
import { OrgGroupActionMenu } from './-components/orgs/org-group-action-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

export const Route = createFileRoute('/_app/orgs')({
  loader: ({ context }) => {
    context.queryClient.prefetchQuery(trpc.orgs.getOrgs.queryOptions())
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()
  const { groupId } = useParams({ strict: false })
  // Orgs access is the static `orgs:read` capability (see usePermissions / canAccessOrgs). The nav link is
  // hidden for anyone without it, but a direct URL hit would otherwise reach the panels' getOrgs suspense
  // query and hard-crash on the backend's INSUFFICIENT_PERMISSIONS — so degrade to a no-access state here.
  const canAccessOrgs = usePermissions().can('orgs:read')
  const titleActions = canAccessOrgs && groupId ? <OrgGroupActionMenu group={{ id: groupId }} variant="header" /> : null

  // Inside a report-tab live preview (/orgs/<id>/groups/<gid>/board …), strip to just the pager pane.
  const previewFrom = useTransitionPreviewFrom()
  if (previewFrom !== null && previewFrom !== '/' && previewFrom.startsWith('/orgs/')) {
    return <ReportPager className="h-full" />
  }

  return (
    <div id="report" className="@container flex h-full flex-col">
      <PageLayout
        icon={NAVIGATION_ICONS.organizations}
        title={t`Administrations`}
        breadcrumbsSlot={canAccessOrgs ? <OrgsBreadcrumbs /> : null}
        titleActions={titleActions}
      >
        {canAccessOrgs ? (
          <OrgsLayout />
        ) : (
          <ScrollableContent>
            <ScrollableArea>
              <PanelEmptyState
                icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
                title={t`No access`}
                description={t`You don't have permission to access this section.`}
              />
            </ScrollableArea>
          </ScrollableContent>
        )}
      </PageLayout>
    </div>
  )
}

function OrgsLayout() {
  const { t } = useLingui()
  const { orgId: orgIdFromParams, groupId } = useParams({ strict: false })
  const { first, second } = useRootSearch()
  // Gate the viewport-driven auto-collapse on hydration: the panel body hydrates inside a Suspense
  // boundary, so flipping collapse from isMobile mid-hydration races that boundary and mismatches.
  const hydrated = useHydrated()
  const isMobile = useIsMobile() && hydrated

  const hasGroupSelected = !!groupId

  const hasProfileSelected =
    first?.type === 'org_group' ||
    first?.type === 'org_user' ||
    second?.type === 'org_group' ||
    second?.type === 'org_user'

  // Level 1: no org selected → the overview lives on the `orgs.index` route; render it through <Outlet/>.
  // (OrgsOverview owns the zero-orgs empty state, so there's no separate branch here.)
  if (!orgIdFromParams) {
    return (
      <ScrollableContent>
        <ScrollableArea>
          <Outlet />
        </ScrollableArea>
      </ScrollableContent>
    )
  }

  // Level 2+: Org selected — groups panel squashes to icon strip when a group is selected,
  // then the users+report panel slides in from the right (same pattern as JobsLayout).
  return (
    <CollapsiblePanelProvider defaultCollapsed={isMobile && hasGroupSelected}>
      <div className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* Left Panel — Groups list (squashes to 52px when a group is selected) */}
        <CollapsibleListPanel isOpen={hasGroupSelected} className="print:hidden">
          <CollapsibleListPanelBorder>
            <CollapsiblePanel expandedWidth="100%" disableTransition>
              <Suspense fallback={<ListPanelBodySkeleton />}>
                <OrgsGroupsPanel />
              </Suspense>
            </CollapsiblePanel>
          </CollapsibleListPanelBorder>
        </CollapsibleListPanel>

        {/* Right Panel Group — slides in when a group is selected */}
        <SlideInPanel isOpen={hasGroupSelected} innerClassName="flex">
          {/* Middle Panel — Users list (collapsible with ] shortcut) */}
          <CollapsiblePanelProvider
            cookieName="panel_state_orgs_users"
            keyboardShortcut="]"
            defaultCollapsed={isMobile}
          >
            <StickyPanel className="print:hidden">
              <CollapsiblePanel>
                <Suspense fallback={<MembersPanelSkeleton />}>
                  <OrgsUsersPanel />
                </Suspense>
              </CollapsiblePanel>
            </StickyPanel>
          </CollapsiblePanelProvider>

          {/* Right Panel — Report */}
          <ScrollableContent>
            {hasProfileSelected ? (
              <>
                <StickyHeader>
                  <Suspense fallback={<ReportTabsSkeleton />}>
                    <ReportTabs />
                  </Suspense>
                </StickyHeader>
                {/* The `orgs.$orgId.groups.$groupId` layout route renders here via <AnimatedOutlet/>. It
                    lives HERE (not in the `$groupId` layout) so the enter animation plays once when the
                    report panel first appears and never re-animates on tab/group switches — see
                    AnimatedOutlet. */}
                <ScrollableArea>{hasGroupSelected && <ReportPager className="h-full" />}</ScrollableArea>
              </>
            ) : (
              <ScrollableArea>
                <PanelEmptyState
                  icon={<UsersIcon className="text-muted-foreground size-5" />}
                  title={t`Select a user`}
                  description={t`Select a user from the list to view their profile.`}
                />
              </ScrollableArea>
            )}
          </ScrollableContent>
        </SlideInPanel>
      </div>
    </CollapsiblePanelProvider>
  )
}

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

function OrgsBreadcrumbs() {
  const { t } = useLingui()
  const { orgId, groupId } = useParams({ strict: false })
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/orgs"
                search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
              >
                {t`Administrations`}
              </Link>
            }
          />
        </BreadcrumbItem>
        {orgId && (
          <Suspense fallback={null}>
            <OrgCrumb orgId={orgId} />
          </Suspense>
        )}
        {orgId && groupId && (
          <Suspense fallback={null}>
            <GroupCrumb orgId={orgId} groupId={groupId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function OrgCrumb(props: { orgId: string }) {
  const { data } = useSuspenseQuery(trpc.orgs.getOrgs.queryOptions())
  const org = data.find((o) => o.id === props.orgId)
  if (!org?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/orgs/$orgId"
              params={{ orgId: props.orgId }}
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {org.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function GroupCrumb(props: { orgId: string; groupId: string }) {
  const { data } = useSuspenseQuery(trpc.orgs.getGroups.queryOptions(props.orgId))
  const group = data.find((g) => g.id === props.groupId)
  if (!group?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/orgs/$orgId/groups/$groupId/report"
              params={{ orgId: props.orgId, groupId: props.groupId }}
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {group.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function OrgsGroupsPanel() {
  const { orgId: orgIdFromParams } = useParams({ strict: false })
  const [openCreateGroup, setOpenCreateGroup] = useState(false)
  const { data: orgs } = useSuspenseQuery(trpc.orgs.getOrgs.queryOptions())
  const orgId = orgIdFromParams ?? orgs[0]?.id

  return (
    <>
      <CollapsiblePanelContent>
        {orgId && <OrgsGroupsOverview orgId={orgId} onCreateGroup={() => setOpenCreateGroup(true)} variant="sidebar" />}
      </CollapsiblePanelContent>

      <Dialog open={openCreateGroup} onOpenChange={setOpenCreateGroup}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-xl">
          <OrgGroupForm setOpen={setOpenCreateGroup} />
        </DialogContent>
      </Dialog>
    </>
  )
}
